import React from 'react';
import Dropzone from 'react-dropzone';
import { Box, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { SparkButton, SparkProgressBar } from 'sharedComponents/UI';
import {
  ErrorStatusIcon,
  NextIcon,
  SuccessStatusIcon,
  Upload,
} from 'components/SVG';
import {
  ExcelFileIcon,
  JPGFileIcon,
  JSFiledIcon,
  MarkDownFileIcon,
  PDFIcon,
  PNGFileIcon,
  PPTFileIcon,
  RawFileIcon,
  SVGFileIcon,
  TIFFFIleIcon,
  TXTFileIcon,
  WordFileIcon,
} from 'components/SVG/fileType';
import type { UploadType } from 'hooks/useBlobStorage';
import type { Accept, FileRejection } from 'react-dropzone';
import { MAX_FILE_SIZE } from 'constants/index';

const acceptedFileTypes: Accept = {
  'application/pdf': ['.pdf'],
  'application/msword': ['.doc'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': [
    '.docx',
  ],
  'text/plain': ['.txt'],
};

export const fileTypeToIconMap: Record<string, JSX.Element> = {
  pdf: <PDFIcon />,
  md: <MarkDownFileIcon />,
  txt: <TXTFileIcon />,
  tiff: <TIFFFIleIcon />,
  raw: <RawFileIcon />,
  png: <PNGFileIcon />,
  jpg: <JPGFileIcon />,
  jpeg: <JPGFileIcon />,
  js: <JSFiledIcon />,
  docx: <WordFileIcon />,
  xls: <ExcelFileIcon />,
  xlsx: <ExcelFileIcon />,
  ppt: <PPTFileIcon />,
  svg: <SVGFileIcon />,
};

type FileUploadStepProps = {
  handleNextClick: () => void;
  uploads: UploadType[];
  handleFileUpload: (
    acceptedFiles: File[],
    fileRejections: FileRejection[]
  ) => Promise<(string | undefined)[] | undefined>;
  handleDelete: (fileName: string) => Promise<void>;
  shouldLabelRender?: boolean;
  shouldNextButtonRender?: boolean;
  shouldSaveButtonRender?: boolean;
};

function FileUploadStep({
  handleNextClick,
  uploads,
  handleFileUpload,
  handleDelete,
  shouldLabelRender = true,
  shouldNextButtonRender = true,
  shouldSaveButtonRender = false,
}: FileUploadStepProps) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <Dropzone
        onDrop={handleFileUpload}
        multiple={true}
        accept={acceptedFileTypes}
        maxSize={MAX_FILE_SIZE}
      >
        {({ getRootProps, getInputProps }) => (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              width: '100%',
            }}
            {...getRootProps()}
          >
            {shouldLabelRender && (
              <>
                <Typography variant='h6'>UPLOAD FILES</Typography>
                <Typography
                  variant='body2'
                  sx={{ color: '#A3A3A3' }}
                  textAlign='center'
                >
                  Files of type .txt, .pdf, .doc, or .docx and up to 15MB are
                  currently supported.
                </Typography>
              </>
            )}
            <Box
              sx={{
                marginTop: '40px',
                border: '2px dashed #E8E8E8',
                borderRadius: '22px',
                width: '100%',
                minHeight: '250px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                flexDirection: 'column',
                color: '#A3A3A3',
                gap: '15px',
              }}
            >
              <Upload />
              <Typography variant='body2'>
                Drag & drop your files here
              </Typography>
              <Typography variant='body2'>OR</Typography>
              <input {...getInputProps()} />
              <SparkButton variant='contained' onClick={() => {}}>
                Browse Files
              </SparkButton>
            </Box>
          </Box>
        )}
      </Dropzone>

      {!!uploads.length && (
        <Box sx={{ width: '100%', padding: 2 }}>
          {uploads.map((upload, index) => (
            <Box
              key={index}
              display='flex'
              bgcolor={(theme) => theme.palette.borderColor}
              borderRadius='10px'
              padding={2}
              gap={2}
              alignItems='center'
              marginTop={2}
            >
              {fileTypeToIconMap[upload.fileTypeTag] || <RawFileIcon />}
              <Box sx={{ width: '100%' }}>
                <Box>
                  <Typography>{upload.file.name}</Typography>
                  {upload.errorMessage && upload.isError && (
                    <Typography variant='subtitle2' color='error.main'>
                      {upload.errorMessage}
                    </Typography>
                  )}
                </Box>
                {upload.isUploading && (
                  <SparkProgressBar value={upload.progress} />
                )}
                {upload.isSuccessful && (
                  <Typography variant='body2'>{`${
                    upload.file.size / 1000
                  } kb`}</Typography>
                )}
              </Box>
              {upload.isSuccessful && <SuccessStatusIcon />}
              {upload.isError && <ErrorStatusIcon />}
              <IconButton
                aria-label='close'
                onClick={() =>
                  handleDelete(
                    upload.blockBlobClient?.['_name'] || upload.fileName
                  )
                }
                sx={{
                  color: (theme) => theme.palette.primary.main,
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          ))}
        </Box>
      )}
      {shouldNextButtonRender && (
        <Box display='flex' width='100%' padding={2} justifyContent='flex-end'>
          <SparkButton
            onClick={handleNextClick}
            endIcon={shouldSaveButtonRender ? null : <NextIcon />}
            variant='contained'
            sx={{
              marginRight: 2,
              height: '45px',

              borderColor: '#EEEEEE',
            }}
            disabled={
              uploads.some((upload) => upload.isUploading) || !uploads.length
            }
          >
            {shouldSaveButtonRender ? 'Save' : 'Next'}
          </SparkButton>
        </Box>
      )}
    </Box>
  );
}

export default FileUploadStep;
